import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import trim from 'lodash/trim';
import { useTheme } from 'styled-components';

import { HistoryLocation } from 'ia-react-core/~/interfaces/HistoryLocation';
import Router from 'ia-react-core/~/services/router/Router';
import closeModalById from 'ia-react-core/~/actions/modal-fn/closeModalById';
import openModalById from 'ia-react-core/~/actions/modal-fn/openModalById';
import { ReactComponent as WarningIcon } from 'ia-react-core/~/icons/warning.svg';
import useTranslation from 'ia-react-core/~/hooks/useTranslation';
import ExitModal, { ExitModalProps } from '../../modal/ExitModal';

export interface NavigationGuardProps extends ExitModalProps {
  /** Path that is checked against. By default, the current path is used. */
  path?: string;
  /** Use the cancel button as the quit button */
  reverseButtons?: boolean;
  /** Optional callback to custimize the navigation logic. Return a string to navigate to this false, true to allow redirection to next location or false to block the navigation. */
  onNavigate?: (options: { data: { nextLocation: HistoryLocation }; modalId: number; name: string }) => (
    void | boolean | string |
    [string] |
    [string, Object] |
    [string, Object, { replace?: boolean }]
  );
}

const NavigationGuardDefaultProps = {
  reverseButtons: true,
};

const NavigationGuard = <Data extends { nextLocation: HistoryLocation }> ({ modalId, path, reverseButtons, onNavigate, ...props }: NavigationGuardProps) => {
  const { t } = useTranslation('Core.NavigationGuard');
  const theme = useTheme();
  const nextLocationRef = useRef<HistoryLocation>();
  const cancelledRef = useRef(false);
  const history = Router.useHistory();

  const normalizedPath = useMemo(() => path && `/${trim(path.replace('*', ''), '/')}/`, [path]);

  const dispatch = useDispatch();

  const handleQuit = useCallback((options: { data: Data; modalId: number; name: string }) => {
    const result = onNavigate ? onNavigate(options) : true;
    cancelledRef.current = Boolean(result);

    if (result) {
      dispatch(closeModalById(modalId));
      if (Array.isArray(result)) {
        const action = result[2]?.replace ? 'replace' : 'push';
        history[action](result[0], result[1]);
      } else {
        history.push(typeof result === 'string' ? result : `${nextLocationRef.current.pathname}${nextLocationRef.current.search}`);
      }
    }
  }, [dispatch, history, modalId, onNavigate]);

  const modalProps = useMemo(() => ({
    [reverseButtons ? 'onCancel' : 'onConfirm']: handleQuit,
    cancelButton: reverseButtons ? t('cancelButton') : t('confirmButton'),
    confirmButton: reverseButtons ? t('confirmButton') : undefined,
  }), [handleQuit, reverseButtons, t]);

  useEffect(() => {
    const unblock = history.block((newLocation) => {
      if (!cancelledRef.current && (!path || !newLocation.pathname.startsWith(normalizedPath))) {
        nextLocationRef.current = newLocation;
        dispatch(openModalById(modalId, { nextLocation: nextLocationRef.current }));
        return false;
      }

      cancelledRef.current = false;

      return undefined;
    });

    return () => {
      unblock();
    };
  }, [dispatch, history, modalId, normalizedPath, path]);

  return (
    <ExitModal<Data>
      modalId={modalId}
      color={theme.colors.warning}
      Icon={WarningIcon}
      {...props}
      {...modalProps}
    />
  );
};

NavigationGuard.defaultProps = NavigationGuardDefaultProps;

export default NavigationGuard;
